import SecurityEntity, { PermissionSecurity } from 'src/app/models/entity-security.model';
import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { LoggingService } from 'src/app/services/logging.service';
import { environment } from 'src/environments/environment';
import { Entity } from '../models/entity.model';
import { Filter } from '../models/filter.model';
import { RoleSecurity, RoleSecurityEntity } from '../models/role-security.model';
import { UserGroup } from '../models/user-group.model';
import EntitySecurity from 'src/app/models/entity-security.model';
import { Observable, from, of, shareReplay, switchMap, tap } from 'rxjs';
import { ApiService } from './api.service';

export const Entities = {
  Permissions: 'config_permissions_ui',
  Users: 'config_users_ui',
  User: 'config_user_ui',
  Entity: 'config_entity_ui',
  Role: 'config_role_ui',
  DomainValues: 'config_domain-values_ui',
  DomainValue: 'config_domain-value_ui',
  Organizations: 'config_organizations_ui',
  Organization: 'config_organization_ui',
  InvestmentProjects: 'config_investment-projects_ui',
  InvestmentProject: 'config_investment-project_ui',
  Welcome: 'config_welcome_ui',
} as const;

export const Permissions = {
  Create: 'CREATE',
  Read: 'READ',
  Update: 'UPDATE',
  Delete: 'DELETE',
  EnableDisable: 'ENABLE-DISABLE',
  ResetPassword: 'RESET-PASSWORD',
  ChangePassword: 'CHANGE-PASSWORD',
} as const;

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  public can(permission: string, entity: string): Signal<boolean> {
    return computed(() => {
      const entities = this.state();

      return !!(entities as Entity[])
        .filter((x) => x.code === entity)[0]
        ?.permissions.find((y) => y.code === permission);
    });
  }

  public async getAllEntities(): Promise<SecurityEntity[]> {
    this.securityRolesSignal.update(() => []);

    const permissions = await this.API.get(environment.API_NAME, `/permissions`);
    this.securityEntitiesSignal.update(() => permissions);

    return permissions;
  }

  public async getAllRoles(groups: UserGroup[]): Promise<RoleSecurity[]> {
    this.securityEntitiesSignal.update(() => []);

    const permissions = await this.API.get(environment.API_NAME, `/permissions`);

    const roles: RoleSecurity[] = groups.map((group) => {
      const rolesSecurity: RoleSecurityEntity[] = [];
      permissions.forEach((e: EntitySecurity) => {
        e.permissions.forEach((p: PermissionSecurity) => {
          if (p.groups.includes(group.GroupName)) {
            if (rolesSecurity.find((x) => x.entity === e.code)) {
              rolesSecurity.find((x) => x.entity === e.code)?.permissions.push({ code: p.code });
            } else {
              rolesSecurity.push({ entity: e.code, permissions: [{ code: p.code }] });
            }
          }
        });
      });

      return { _id: group.GroupName, code: group.GroupName, description: group.Description, entities: rolesSecurity };
    });

    this.securityRolesSignal.update(() => roles);

    return roles;
  }

  public async getEntities(filter: Filter[]): Promise<SecurityEntity[]> {
    let permissions: SecurityEntity[] = await this.API.get(environment.API_NAME, `/permissions`);
    const value = filter[0].value;

    if (filter[0].operator === 'eq') {
      permissions = permissions.filter((permission) => permission.code === value);
    } else {
      permissions = permissions.filter((permission) => permission.code !== value);
    }

    this.securityEntitiesSignal.update(() => permissions);

    return permissions;
  }

  public async getRoles(filter: Filter[], groups: UserGroup[]): Promise<RoleSecurity[]> {
    const permissions: SecurityEntity[] = await this.API.get(environment.API_NAME, `/permissions`);
    const value = filter[0].value;

    const roles: RoleSecurity[] = [];

    if (filter[0].operator === 'eq') {
      groups = groups.filter((group) => group.GroupName === value);
    } else {
      groups = groups.filter((group) => group.GroupName !== value);
    }

    groups.forEach((group) => {
      const rolesSecurity: RoleSecurityEntity[] = [];
      permissions.forEach((e: EntitySecurity) => {
        e.permissions.forEach((p: PermissionSecurity) => {
          if (p.groups.includes(group.GroupName)) {
            if (rolesSecurity.find((x) => x.entity === e.code)) {
              rolesSecurity.find((x) => x.entity === e.code)?.permissions.push({ code: p.code });
            } else {
              rolesSecurity.push({ entity: e.code, permissions: [{ code: p.code }] });
            }
          }
        });
      });

      roles.push({
        _id: group.GroupName,
        code: group.GroupName,
        description: group.Description,
        entities: rolesSecurity,
      });
    });

    this.securityRolesSignal.update(() => roles);

    return roles;
  }

  public async updatePermissionsForRole(
    role: string,
    entity: {
      entity: string;
      permissions: {
        code: string;
        description: string | undefined;
        group: string;
        selected: boolean | undefined;
      }[];
      [key: string]: any;
    },
  ): Promise<void> {
    if (!role || !entity) {
      throw new Error('role and entity are mandatory');
    }

    this.logger.debug('updatePermissionsForRole', role, entity);
    await this.API.patch(environment.API_NAME, `/permissions/${role}`, { body: entity });
  }

  public loadPermissions(): Observable<Entity[]> {
    if (this.state().length) {
      return of(this.state());
    }

    return of(null).pipe(
      shareReplay({ refCount: true }),
      switchMap(() => from(this.API.get(environment.API_NAME, `/permissions/for-user-groups`))),
      tap((entities) => this.state.set(entities)),
    );
  }

  private readonly logger = inject(LoggingService);
  private readonly API = inject(ApiService);
  private readonly state = signal<Entity[]>([]);
  private readonly securityEntitiesSignal = signal<SecurityEntity[]>([]);
  private readonly securityRolesSignal = signal<RoleSecurity[]>([]);

  public securityEntities = computed(() => this.securityEntitiesSignal());
  public securityRoles = computed(() => this.securityRolesSignal());
}
